import { Form } from 'antd';
import { FormInstance } from 'antd/lib/form';
import { isEqual } from 'lodash';
import React from 'react';
import i18n from '../../../i18n/config';
import { Company } from '../../../models';
import { CompanyAccessGrantInput, ErrorMessages, ModalBaseProps, Option } from '../../../shared';
import { HasModal, HasMultipleSelectDropdown, HasParagraph, HasTextInput } from '../../atoms';

interface CompanyAccessInviteProps extends ModalBaseProps<CompanyAccessGrantInput> {
  companies: Company[];
}

interface CompanyAccessInviteState {
  updateInProgress: boolean;
}

class HasCompanyAccessInviteModal extends React.Component<CompanyAccessInviteProps, CompanyAccessInviteState> {
  state = { updateInProgress: false };

  private formRef = React.createRef<FormInstance>();

  componentDidUpdate(prevProps: Readonly<CompanyAccessInviteProps>) {
    if (!isEqual(prevProps, this.props)) {
      this.setState({ updateInProgress: false });
    }
  }

  onCancel = () => {
    this.formRef.current?.resetFields();
    this.props.onCancel();
  };

  onOk = (values: any) => {
    this.setState({ updateInProgress: true });
    const input: CompanyAccessGrantInput = { email: values.email, companyIds: values.companies };
    this.props.onOk(input).then(
      () => {
        this.setState({ updateInProgress: false });
        this.formRef.current?.resetFields();
      },
      () => this.setState({ updateInProgress: false })
    );
  };

  render() {
    const { visible, companies } = this.props;
    return (
      <HasModal
        title={<HasParagraph content={i18n.t('companyAccess.grantModal.message')} style={{ marginBottom: '10px' }} />}
        okText={i18n.t('companyAccess.grant')}
        cancelText={i18n.t('shared.cancel')}
        onCancel={this.onCancel}
        closable={false}
        visible={visible}
        okButtonProps={{ loading: this.state.updateInProgress, htmlType: 'submit', form: 'company-access-form' }}
        cancelButtonProps={{ disabled: this.state.updateInProgress }}
      >
        <Form
          id="company-access-form"
          ref={this.formRef}
          onFinish={this.onOk}
          hideRequiredMark
          layout="vertical"
          style={{ padding: '0px 80px' }}
        >
          <Form.Item
            name="email"
            label={i18n.t('shared.email')}
            colon={false}
            hasFeedback
            rules={[
              { required: true, message: ErrorMessages.INPUT_REQUIRED(i18n.t('shared.emailAddress').toLowerCase()) },
              { whitespace: true, message: ErrorMessages.INPUT_REQUIRED(i18n.t('shared.emailAddress').toLowerCase()) },
              { type: 'email', message: ErrorMessages.EMAIL_INVALID },
            ]}
          >
            <HasTextInput key="email" size="large" />
          </Form.Item>
          <Form.Item
            name="companies"
            label={i18n.t('companyAccess.grantModal.companies')}
            colon={false}
            rules={[
              {
                required: true,
                message: ErrorMessages.INPUT_REQUIRED(i18n.t('companyAccess.grantModal.companies').toLowerCase()),
              },
            ]}
          >
            <HasMultipleSelectDropdown<Company>
              mode="multiple"
              showSearch
              optionFilterProp="label"
              size="large"
              style={{ width: '100%' }}
              options={companies.map<Option<Company>>((company) => ({
                value: company.id,
                label: company.name,
                forObject: company,
              }))}
            />
          </Form.Item>
        </Form>
      </HasModal>
    );
  }
}

export default HasCompanyAccessInviteModal;
